/**
 * Handles ChunkLoadError (stale webpack chunks after deployment).
 *
 * When nuxt generate produces new builds, old chunk filenames become invalid.
 * Users who already had the site open will hit ChunkLoadError on navigation.
 * This plugin detects those errors and performs a clean full-page reload
 * so the browser fetches the current HTML with up-to-date chunk references.
 *
 * A sessionStorage flag prevents infinite reload loops if the error persists.
 */

const RELOAD_KEY = 'mikro_chunk_reload'
const RELOAD_MAX = 2
const RELOAD_TTL_MS = 30_000

function isChunkError(error) {
  if (!error) return false
  const name = error.name || ''
  const message = error.message || ''
  return (
    name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('ChunkLoadError') ||
    (name === 'TypeError' && message.includes('dynamically imported module'))
  )
}

function getReloadCount() {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY)
    if (!raw) return 0
    const { count, ts } = JSON.parse(raw)
    if (Date.now() - ts > RELOAD_TTL_MS) return 0
    return count || 0
  } catch {
    return 0
  }
}

function setReloadCount(count) {
  try {
    sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ count, ts: Date.now() }))
  } catch {
    // sessionStorage unavailable — accept the risk of one extra reload
  }
}

function clearReloadCount() {
  try {
    sessionStorage.removeItem(RELOAD_KEY)
  } catch {
    // no-op
  }
}

function forceReload() {
  const count = getReloadCount()
  if (count >= RELOAD_MAX) {
    console.warn('[chunk-error-handler] Max reload attempts reached, showing error page')
    return false
  }
  setReloadCount(count + 1)
  window.location.reload(true)
  return true
}

export default ({ app }) => {
  // Clear the reload counter on successful page load
  clearReloadCount()

  // Catch unhandled chunk errors from dynamic imports (route lazy-loading)
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      if (isChunkError(event.reason)) {
        event.preventDefault()
        console.warn('[chunk-error-handler] Stale chunk detected via unhandledrejection, reloading…')
        forceReload()
      }
    })
  }

  // Intercept Vue Router navigation errors (most common path for chunk failures)
  const router = app.router
  if (router) {
    router.onError((error) => {
      if (isChunkError(error)) {
        console.warn('[chunk-error-handler] Stale chunk detected via router.onError, reloading…')
        forceReload()
      }
    })
  }
}
